    import React, { useEffect, useRef, useState } from 'react';
    import PropTypes from 'prop-types';
    import { MarkerClusterer } from "@googlemaps/markerclusterer";
    import { renderToString } from 'react-dom/server';
import i18n from '../multilingual/config';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import ReligiousPlaceDetailsComponent from './ReligiousPlaceDetailsComponent';

const GoogleMap = ({
        locations = [],
        center,
        zoom = 12,
        mapContainerStyle = { width: '100%', height: '500px' },
        loadingComponent = <div>Loading map...</div>,
        onMapClick,
        onMarkerClick,
        clusterMarkers = true,
        mapOptions = {},
        markerOptions = {},
        infoWindowOptions = {}
    }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markers = useRef([]);
    const infoWindows = useRef([]);
    const markerClusterer = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [placeDetailsAccessKey, setPlaceDetailsAccessKey] = useState()
    const [isDialogVisible, setIsDialogVisible] = useState(false)

    // Load Google Maps script
    useEffect(() => {
        if (window.google && window.google.maps) {
            setMapLoaded(true);
            return;
        }

        if (window.googleMapsLoading) return;
        window.googleMapsLoading = true;

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            setMapLoaded(true);
            window.googleMapsLoading = false;
        };
        script.onerror = () => {
            setMapError('Failed to load Google Maps API');
            window.googleMapsLoading = false;
        };

        document.head.appendChild(script);

        // Load MarkerClusterer if needed
        const clusterScript = document.createElement('script');
        if (clusterMarkers && !window.markerClusterer) {
            clusterScript.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
            clusterScript.async = true;
            document.head.appendChild(clusterScript);
        }

        return () => {
            document.head.removeChild(script);
            if (clusterScript) document.head.removeChild(clusterScript);
        };
    }, [clusterMarkers]);

    // Initialize map when script is loaded
    useEffect(() => {
        if (!mapLoaded || !window.google || !mapRef.current) return;

        const defaultMapOptions = {
            center: center || (locations.length > 0 ? 
                { lat: Number(locations[0].lat), lng: Number(locations[0].lng) } : 
                { lat: 0, lng: 0 }),
            zoom,
            mapId: 'places_map_id',
            ...mapOptions
        };

        mapInstance.current = new window.google.maps.Map(mapRef.current, defaultMapOptions);

        // Add click listener if provided
        if (onMapClick) {
            mapInstance.current.addListener('click', onMapClick);
        }

        // Handle window resize
        const handleResize = () => {
            if (mapInstance.current) {
                window.google.maps.event.trigger(mapInstance.current, 'resize');
                if (center) mapInstance.current.setCenter(center);
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (onMapClick) {
                window.google.maps.event.clearListeners(mapInstance.current, 'click');
            }
            clearMarkers();
        };
    }, [mapLoaded]);

    // Update markers when locations change
    useEffect(() => {
        if (!mapLoaded || !mapInstance.current || !locations.length) return;

        clearMarkers();
        createMarkers();

        // Adjust map center if center prop is provided
        if (center) {
            mapInstance.current.setCenter(center);
        }
    }, [locations, center, mapLoaded]);

    const clearMarkers = () => {
        // Clear existing markers
        markers.current.forEach(marker => marker.setMap(null));
        markers.current = [];
        
        // Clear existing info windows
        infoWindows.current.forEach(window => window.close());
        infoWindows.current = [];
        
        // Clear clusterer if exists
        if (markerClusterer.current) {
            markerClusterer.current.clearMarkers();
        }
    };

    const infoWindowHTML = (location) => {
        return (
            <div className='flex flex-column gap-2'>
                <div className=''>
                    {i18n.language === 'en' ? location.religion_name_en : location.language === 'es' ? location.religion_name_es : location.religion_name_pt_br}
                </div>
                <div className=''>
                    {location.address}
                </div>
                <div className=''>
                    Distance: {i18n.language === 'en' ? (Math.round(Number(location.distance_miles)) + ' miles')  : (Math.round(Number(location.distance_miles) * 1.60934) + ' km')}
                </div>
            </div>
        )
    }

    const createMarkers = async () => {
        const {AdvancedMarkerElement} = await window.google.maps.importLibrary("marker")
        const bounds = locations?.length > 1 ? new window.google.maps.LatLngBounds() : null;
    
        const newMarkers = locations.map((location, i) => {
            const label = locations.length > 1 ? (i+1).toString() : '' ;
            const pinGlyph = new window.google.maps.marker.PinElement({
                glyph: label,
                glyphColor: "white",
            });
            const marker = new AdvancedMarkerElement({
                position: { 
                    lat: Number(location.lat), 
                    lng: Number(location.lng) 
                },
                map: clusterMarkers ? null : mapInstance.current,
                title: location.name,
                content: pinGlyph.element,
                ...markerOptions
            });

            // Add info window if content is provided
            // const infoWindow = new window.google.maps.InfoWindow({
            //     content: renderToString(infoWindowHTML(location)),
            //     headerContent: location.name,
            //     ...infoWindowOptions
            // });

            marker.addListener('click', () => {
                // Close all other info windows
                // infoWindows.current.forEach(window => window.close());
                // infoWindow.open(mapInstance.current, marker);
                // if (onMarkerClick) onMarkerClick(location, marker, infoWindow);
                if (onMarkerClick) onMarkerClick(location, marker);
                handleOpenDetails(location)
            });

            // infoWindows.current.push(infoWindow);

            bounds && bounds.extend({
                lat: Number(location.lat),
                lng: Number(location.lng)
            });

            return marker;
        });

        markers.current = newMarkers;

        // Apply clustering if enabled
        new MarkerClusterer({
            map: mapInstance.current,
            markers: newMarkers,
        });

        bounds && mapInstance.current.fitBounds(bounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50
        })
    };

    const handleOpenDetails = (location) => {
        console.log(location.access_key)
        if (location.access_key) {
            setPlaceDetailsAccessKey(location.access_key)
            setIsDialogVisible(true)
        }
    }

    if (mapError) return <div className="map-error">{mapError}</div>;
    if (!mapLoaded) return loadingComponent;

    return (
        <div>
            <div 
                ref={mapRef} 
                style={mapContainerStyle}
                className={`google-map-container w-full`}
            />

            <Dialog visible={isDialogVisible} className='w-11' header onHide={()=>setIsDialogVisible(false)}>
                <ReligiousPlaceDetailsComponent access_key={placeDetailsAccessKey} />
            </Dialog>
        </div>
    );
};

GoogleMap.propTypes = {
    locations: PropTypes.arrayOf(
        PropTypes.shape({
        lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        lng: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
        infoWindowContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        })
    ),
    center: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
    }),
    zoom: PropTypes.number,
    mapContainerStyle: PropTypes.object,
    loadingComponent: PropTypes.node,
    onMapClick: PropTypes.func,
    onMarkerClick: PropTypes.func,
    clusterMarkers: PropTypes.bool,
    mapOptions: PropTypes.object,
    markerOptions: PropTypes.object,
    infoWindowOptions: PropTypes.object,
};

export default GoogleMap;